import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { useParams } from 'react-router-dom';
import '../styles/Lead.css';
import { formatDate, formatPrice } from '../utils/formatterUtils';

function LeadVendite() {
  const { id } = useParams();
  const [vendite, setVendite] = useState([]);
  const [alloggiVendite, setAlloggiVendite] = useState([]);
  const [alloggiDisponibili, setAlloggiDisponibili] = useState([]);
  const [boxDisponibili, setBoxDisponibili] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    alloggio: '',
    piano: '',
    mq: '',
    prezzo_listino: '',
    prezzo_vendita: '',
    varianti: '',
    prezzo_varianti: '',
    nome_cliente: '',
    cognome_cliente: '',
    codice_fiscale: '',
    data_compromesso: '',
    data_rogito: '',
    garage_presente: false,
    nome_garage: '',
    prezzo_garage: '',
    solo_garage: false,
    cliente_box: '',
  });
  const [sort, setSort] = useState('data_compromesso');

  const fetchVendite = useCallback(async () => {
    const res = await api.get(`/lead-vendite/${id}`);
    setVendite(res.data);
  }, [id]);

  const fetchAlloggiVendite = useCallback(async () => {
    try {
      // Ora prendiamo i dati dalla tabella lead-vendite (NON da vendite)
      const res = await api.get(`/lead-vendite/${id}`);
      const leads = Array.isArray(res.data) ? res.data : [];
      setAlloggiVendite(leads);

      // Opzioni alloggi deduplicate basate sui lead
      const seenAlloggi = new Set();
      const alloggi = [];
      for (const v of leads) {
        if (!v.alloggio) continue;
        if (seenAlloggi.has(v.alloggio)) continue;
        seenAlloggi.add(v.alloggio);
        alloggi.push({
          id: v.id,
          alloggio: v.alloggio,
          piano: v.piano || '',
          mq: v.mq || '',
          prezzo_listino: v.prezzo_listino || ''
        });
      }
      setAlloggiDisponibili(alloggi);

      // Opzioni box (garage) deduplicate basate sui lead
      const seenBox = new Set();
      const boxes = [];
      for (const v of leads) {
        if (!v.nome_garage) continue;
        if (seenBox.has(v.nome_garage)) continue;
        seenBox.add(v.nome_garage);
        boxes.push({ id: v.id, alloggio: v.nome_garage });
      }
      setBoxDisponibili(boxes);
    } catch (e) {
      console.error('[fetchAlloggiVendite] errore', e);
      setAlloggiVendite([]);
      setAlloggiDisponibili([]);
      setBoxDisponibili([]);
    }
  }, [id]);

  useEffect(() => {
    fetchVendite();
    fetchAlloggiVendite();
  }, [fetchVendite, fetchAlloggiVendite]);


  const prezzoMedio = vendite.length
    ? vendite.reduce((sum, v) => sum + parseFloat(v.prezzo_mq || 0), 0) / vendite.length
    : 0;

  const sortedVendite = [...vendite].sort((a,b)=>{
    if(sort==='alloggio') return a.alloggio.localeCompare(b.alloggio);
    if(sort==='piano') {
      const order = { 'T':0, '1':1, '2':2, '3':3, '4':4 };
      return (order[a.piano]||99) - (order[b.piano]||99);
    }
    return new Date(a.data_compromesso) - new Date(b.data_compromesso);
  });

  const openForm = (vendita=null) => {
    if(vendita){
      setFormData({
        ...vendita,
        alloggio: vendita.alloggio || '',
        piano: vendita.piano || '',
        mq: vendita.mq || '',
        prezzo_listino: vendita.prezzo_listino || '',
        prezzo_vendita: vendita.prezzo_vendita || '',
        varianti: vendita.varianti || '',
        prezzo_varianti: vendita.prezzo_varianti || '',
        nome_cliente: vendita.nome_cliente || '',
        cognome_cliente: vendita.cognome_cliente || '',
        codice_fiscale: vendita.codice_fiscale || '',
        data_compromesso: vendita.data_compromesso ? vendita.data_compromesso.split('T')[0] : '',
        data_rogito: vendita.data_rogito ? vendita.data_rogito.split('T')[0] : '',
        garage_presente: !!vendita.garage_presente,
        nome_garage: vendita.nome_garage || '',
        prezzo_garage: vendita.prezzo_garage || '',
        solo_garage: !!vendita.solo_garage,
        cliente_box: vendita.cliente_box || ''
      });
      setEditing(vendita.id);
    } else {
      setFormData({
        alloggio:'',piano:'',mq:'',prezzo_listino:'',prezzo_vendita:'',varianti:'',prezzo_varianti:'',nome_cliente:'',cognome_cliente:'',codice_fiscale:'',data_compromesso:'',data_rogito:'',
        garage_presente: false,
        nome_garage: '',
        prezzo_garage: '',
        solo_garage: false,
        cliente_box: '',
      });
      setEditing(null);
    }
    setShowForm(true);
  };

  const handleChange = e => {
    const { name, value } = e.target;
    let updated = { ...formData, [name]: value };

    // Se cambia l'alloggio, popola automaticamente piano, mq e prezzo listino
    if (name === 'alloggio') {
      // Cerca prima nelle opzioni dedotte dai lead, poi nei lead stessi come fallback
      const selezionato = alloggiDisponibili.find(a => a.alloggio === value)
        || vendite.find(a => a.alloggio === value);

      if (selezionato) {
        updated = {
          ...updated,
          piano: selezionato.piano || '',
          mq: (selezionato.mq ?? selezionato.mq_commerciali) || '',
          prezzo_listino: (selezionato.prezzo_listino ?? selezionato.listino_alloggio) || ''
        };
      } else {
        updated = { ...updated, piano: '', mq: '', prezzo_listino: '' };
      }
    }

    setFormData(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...formData,
      alloggio: formData.alloggio || null,
      piano: formData.piano || null,
      mq: formData.mq ? parseFloat(formData.mq) : null,
      prezzo_listino: formData.prezzo_listino ? parseFloat(formData.prezzo_listino) : null,
      prezzo_vendita: formData.prezzo_vendita ? parseFloat(formData.prezzo_vendita) : null,
      varianti: formData.varianti || null,
      prezzo_varianti: formData.prezzo_varianti ? parseFloat(formData.prezzo_varianti) : null,
      nome_cliente: formData.nome_cliente || null,
      cognome_cliente: formData.cognome_cliente || null,
      codice_fiscale: formData.codice_fiscale || null,
      data_compromesso: formData.data_compromesso || null,
      data_rogito: formData.data_rogito || null,
      prezzo_garage: formData.prezzo_garage ? parseFloat(formData.prezzo_garage) : null,
      solo_garage: formData.solo_garage,
      cliente_box: formData.cliente_box || null,
      cantiere_id: id
    };
    if(editing){
      await api.put(`/lead-vendite/${editing}`, payload);
    } else {
      await api.post(`/lead-vendite`, payload);
    }
    setShowForm(false);
    fetchVendite();
    fetchAlloggiVendite();
  };

  const handleDelete = async (idVendita) => {
    if(window.confirm('Eliminare questa vendita?')){
      await api.delete(`/lead-vendite/${idVendita}`);
      fetchVendite();
      fetchAlloggiVendite();
    }
  };

  const alloggioOptions = [...alloggiDisponibili];
  if (formData.alloggio && !alloggioOptions.some(a => a.alloggio === formData.alloggio)) {
    const found = alloggiVendite.find(a => a.alloggio === formData.alloggio);
    if (found) alloggioOptions.push(found);
  }
  const boxOptions = [...boxDisponibili];
  if (formData.nome_garage && !boxOptions.some(a => a.alloggio === formData.nome_garage)) {
    const found = alloggiVendite.find(a => a.alloggio === formData.nome_garage);
    if (found) boxOptions.push(found);
  }

  return (
    <div className="sezione">
      <div className="sezione-title">
        <h2 style={{margin:0,color:'#cca667'}}>Vendite</h2>
        <span className="prezzoMedio">Prezzo medio al mq di vendita: {prezzoMedio.toFixed(2)} €</span>
      </div>
      <div className="sezione-sort">
        <select className="sort-select" value={sort} onChange={e=>setSort(e.target.value)}>
          <option value="data_compromesso">Ordina per data</option>
          <option value="alloggio">Ordina per alloggio</option>
          <option value="piano">Ordina per piano</option>
        </select>
        <button type="button" className="btn-aggiungi" onClick={()=>openForm()}>➕ Aggiungi Vendita</button>
      </div>
      {showForm && (
        <div className="modal-overlay">
          <div className="modal-vendite">
            <button type="button" className="close-button" onClick={() => setShowForm(false)}>✖</button>
            <form onSubmit={handleSubmit} className="form-vendita">
              <div className="form-grid-lead">
                <div className="form-group-lead">
                  <label htmlFor="alloggio">Alloggio</label>
                  <select className="custom-select" name="alloggio" id="alloggio" value={formData.alloggio} onChange={handleChange} required={!formData.solo_garage}>
                    <option value="">Seleziona Alloggio</option>
                    {alloggioOptions.map(a => (
                      <option key={a.id} value={a.alloggio}>{a.alloggio}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group-lead">
                  <label htmlFor="piano">Piano</label>
                  <input name="piano" id="piano" placeholder="Piano" value={formData.piano} onChange={handleChange} required={!formData.solo_garage} />
                </div>
                <div className="form-group-lead">
                  <label htmlFor="mq">MQ</label>
                  <input type="number" name="mq" id="mq" placeholder="MQ" value={formData.mq} onChange={handleChange} required={!formData.solo_garage} />
                </div>
                <div className="form-group-lead">
                  <label htmlFor="prezzo_listino">Prezzo Listino</label>
                  <input type="number" name="prezzo_listino" id="prezzo_listino" placeholder="Prezzo Listino" value={formData.prezzo_listino} onChange={handleChange} required={!formData.solo_garage} />
                </div>
                <div className="form-group-lead">
                  <label htmlFor="prezzo_vendita">Prezzo Vendita</label>
                  <input type="number" name="prezzo_vendita" id="prezzo_vendita" placeholder="Prezzo Vendita" value={formData.prezzo_vendita} onChange={handleChange} required={!formData.solo_garage} />
                </div>
                <div className="form-group-lead">
                  <label htmlFor="varianti">Varianti</label>
                  <input name="varianti" id="varianti" placeholder="Varianti" value={formData.varianti} onChange={handleChange} />
                </div>
                <div className="form-group-lead">
                  <label htmlFor="prezzo_varianti">Prezzo Varianti</label>
                  <input type="number" name="prezzo_varianti" id="prezzo_varianti" placeholder="Prezzo Varianti" value={formData.prezzo_varianti} onChange={handleChange} />
                </div>
                {!formData.solo_garage && (
                  <>
                    <div className="form-group-lead">
                      <label htmlFor="nome_cliente">Nome Cliente</label>
                      <input name="nome_cliente" id="nome_cliente" placeholder="Nome Cliente" value={formData.nome_cliente} onChange={handleChange} required={!formData.solo_garage} />
                    </div>
                    <div className="form-group-lead">
                      <label htmlFor="cognome_cliente">Cognome Cliente</label>
                      <input name="cognome_cliente" id="cognome_cliente" placeholder="Cognome Cliente" value={formData.cognome_cliente} onChange={handleChange} required={!formData.solo_garage} />
                    </div>
                    <div className="form-group-lead">
                      <label htmlFor="codice_fiscale">Codice Fiscale Cliente</label>
                      <input name="codice_fiscale" id="codice_fiscale" placeholder="Codice Fiscale Cliente" value={formData.codice_fiscale} onChange={handleChange} required={!formData.solo_garage} />
                    </div>
                    <div className="form-group-lead">
                      <label htmlFor="data_compromesso">Data Compromesso</label>
                      <input type="date" name="data_compromesso" id="data_compromesso" placeholder="Data Compromesso" value={formData.data_compromesso} onChange={handleChange} required={!formData.solo_garage} />
                    </div>
                    <div className="form-group-lead">
                      <label htmlFor="data_rogito">Data Rogito</label>
                      <input type="date" name="data_rogito" id="data_rogito" placeholder="Data Rogito" value={formData.data_rogito} onChange={handleChange} required={!formData.solo_garage} />
                    </div>
                  </>
                )}
                <div className="checkbox-wrapper form-grid-lead checkbox-row">
                  <input
                      type="checkbox"
                      id="garage"
                      checked={formData.garage_presente || false}
                      onChange={e =>
                          setFormData({
                            ...formData,
                            garage_presente: e.target.checked,
                            solo_garage: false,
                            nome_garage: '',
                            prezzo_garage: '',
                            cliente_box: ''
                          })
                      }
                  />
                  <label htmlFor="garage">Garage</label>
                </div>
                <div className="checkbox-wrapper form-grid-lead checkbox-row">
                  <input
                      type="checkbox"
                      checked={formData.solo_garage || false}
                      onChange={(e) => {
                        const soloGarageChecked = e.target.checked;

                        setFormData((prevData) => ({
                          ...prevData,
                          solo_garage: soloGarageChecked,
                          garage_presente: soloGarageChecked ? true : prevData.garage_presente,
                          cliente_box: soloGarageChecked ? prevData.cliente_box : ''
                        }));
                      }}
                  />
                  <label htmlFor="solo_garage">Venduto solo garage?</label>
                </div>
                {formData.garage_presente && (
                  <>
                    <div className="form-group-lead">
                      <label htmlFor="nome_garage">Nome Garage</label>
                      <select className="custom-select" name="nome_garage" id="nome_garage" value={formData.nome_garage} onChange={handleChange} required={formData.solo_garage}>
                        <option value="">Seleziona Garage</option>
                        {boxOptions.map(a => (
                          <option key={a.id} value={a.alloggio}>{a.alloggio}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group-lead">
                      <label htmlFor="prezzo_garage">Prezzo Garage</label>
                      <input type="number" name="prezzo_garage" id="prezzo_garage" placeholder="Prezzo Garage" value={formData.prezzo_garage || ''} onChange={handleChange} required={formData.solo_garage} />
                    </div>

                    {formData.solo_garage && (
                      <div className="form-group-lead">
                        <label htmlFor="cliente_box">Nome e Cognome Cliente Box</label>
                        <input name="cliente_box" id="cliente_box" placeholder="Nome e Cognome Cliente Box" value={formData.cliente_box} onChange={handleChange} required={formData.solo_garage} />
                      </div>
                    )}
                  </>
                )}
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-annulla" onClick={()=>setShowForm(false)}>Annulla</button>
                <button type="submit" className="btn-aggiungi">Salva</button>
              </div>
            </form>
          </div>
        </div>
      )}
      <div className="card-container">
        {sortedVendite.map(v=> (
          <div className="card" key={v.id}>
            <div className="card-header">Alloggio: {v.alloggio} · Piano: {v.piano}
              <div>
                <button type="button" onClick={()=>openForm(v)}>✏️</button>
                <button type="button" onClick={()=>handleDelete(v.id)}>🗑️</button>
              </div>
            </div>
            <div className="card-body">
              <p><strong>MQ:</strong> {v.mq}</p>
              <p><strong>Prezzo al MQ:</strong> € {formatPrice(v.prezzo_mq)}</p>
              <p><strong>Prezzo Listino:</strong> € {formatPrice(v.prezzo_listino)}</p>
              <p><strong>Prezzo Vendita:</strong> € {formatPrice(v.prezzo_vendita)}</p>
              <p><strong>Varianti:</strong> {v.varianti}</p>
              <p><strong>Prezzo Varianti:</strong> € {formatPrice(v.prezzo_varianti)}</p>
              {v.garage_presente && (
                <>
                  <p><strong>Nome Garage:</strong> {v.nome_garage}</p>
                  <p><strong>Prezzo Garage:</strong> € {formatPrice(v.prezzo_garage)}</p>
                </>
              )}
              {v.solo_garage ? (
                <p><strong>Cliente Box:</strong> {v.cliente_box}</p>
              ) : (
                <>
                  <p><strong>Nome Cliente:</strong> {v.nome_cliente}</p>
                  <p><strong>Cognome Cliente:</strong> {v.cognome_cliente}</p>
                  <p><strong>Codice Fiscale:</strong> {v.codice_fiscale}</p>
                  <p><strong>Data Compromesso:</strong> {formatDate(v.data_compromesso)}</p>
                  <p><strong>Data Rogito:</strong> {formatDate(v.data_rogito)}</p>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default LeadVendite;
